import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import "./styles.css";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const SyDialog = ({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = "md",
  className = "",
}) => {
  const panelRef = useRef(null);
  const restoreRef = useRef(null);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();

        return;
      }

      if (event.key !== "Tab") return;

      const panel = panelRef.current;
      if (!panel) return;

      const targets = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
        (node) => node.offsetParent !== null
      );

      if (!targets.length) return;

      const first = targets[0];
      const last = targets[targets.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return undefined;

    restoreRef.current = document.activeElement;

    const panel = panelRef.current;
    (panel?.querySelector(FOCUSABLE) || panel)?.focus?.();

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = overflow;
      restoreRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="sy-dlg-root">
      <button type="button" className="sy-dlg-scrim" aria-label="Close" onClick={onClose} />

      <div
        ref={panelRef}
        className={`sy-dlg-panel sy-dlg-${size} ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header className="sy-dlg-head">
          <div className="sy-dlg-heading">
            <h2 className="sy-dlg-title">{title}</h2>
            {subtitle && <p className="sy-dlg-sub">{subtitle}</p>}
          </div>

          <button type="button" className="sy-dlg-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
        </header>

        <div className="sy-dlg-body">{children}</div>

        {footer && <footer className="sy-dlg-foot">{footer}</footer>}
      </div>
    </div>,
    document.body
  );
};

export const ConfirmDialog = ({
  open = true,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  tone = "danger",
  busy = false,
  onConfirm,
  onCancel,
}) => (
  <SyDialog
    open={open}
    title={title}
    size="sm"
    className="sy-dlg-confirm"
    onClose={onCancel}
    footer={
      <div className="sy-dlg-actions">
        <button type="button" className="sy-flow-btn sy-flow-btn-ghost" onClick={onCancel}>
          {cancelText}
        </button>

        <button
          type="button"
          className={`sy-flow-btn sy-flow-btn-${tone === "danger" ? "danger" : "primary"}`}
          disabled={busy}
          onClick={onConfirm}
        >
          {busy ? "Working…" : confirmText}
        </button>
      </div>
    }
  >
    <div className={`sy-dlg-mark sy-dlg-mark-${tone}`} aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d="M12 8v5" />
        <circle cx="12" cy="16.4" r="0.9" />
        <path d="M12 3.4 21 19.6H3L12 3.4Z" />
      </svg>
    </div>

    <p className="sy-dlg-message">{message}</p>
  </SyDialog>
);

export default SyDialog;
